/**
 * tsa-destination-modal.js: <tsa-destination-modal> custom element.
 *
 * Contract:
 *   - Opens on click of any [data-open-picker] element anywhere on the page
 *     (tsa-how-it-works.liquid, tsa-why-us.liquid, tsa-esim-explainer.liquid
 *     all render one such trigger).
 *   - Closes on overlay click, close button click, or Escape.
 *   - Traps focus and locks body scroll while open (mirrors cart-drawer.js).
 *   - Restores scroll position and focus to the trigger on close.
 *   - Sets aria-expanded on the trigger that opened it.
 *   - Reads country data from window.__TSA_COUNTRIES (set by
 *     sections/tsa-hero-destination.liquid's inline script).
 */
(function () {
  'use strict';

  function isVisible(el) {
    return !!(el.offsetWidth || el.offsetHeight || el.getClientRects().length);
  }

  function getFocusableElements(container) {
    if (!container) return [];
    var selector =
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
    return Array.prototype.slice.call(container.querySelectorAll(selector)).filter(isVisible);
  }

  class TsaDestinationModalElement extends HTMLElement {
    constructor() {
      super();
      this.overlay = this.querySelector('[data-modal-overlay]');
      this.panel = this.querySelector('[data-modal-panel]');
      this.closeBtn = this.querySelector('[data-modal-close]');
      this.input = this.querySelector('[data-modal-input]');
      this.dropdown = this.querySelector('[data-modal-dropdown]');
      this.searchBar = this.querySelector('[data-modal-searchbar]');
      this.grid = this.querySelector('[data-modal-grid]');
      this.triggerEl = null;
      this.activeIndex = -1;
      this.savedScrollY = 0;
      this.onKeydown = this.onKeydown.bind(this);
      this.onDocumentClick = this.onDocumentClick.bind(this);
    }

    connectedCallback() {
      if (this.overlay) {
        this.overlay.addEventListener('click', (event) => {
          if (event.target === this.overlay) this.close();
        });
      }
      if (this.closeBtn) this.closeBtn.addEventListener('click', () => this.close());
      if (this.input) {
        this.input.addEventListener('input', () => {
          this.activeIndex = -1;
          this.renderDropdown(this.input.value);
        });
        this.input.addEventListener('focus', () => {
          if (this.input.value.trim()) this.renderDropdown(this.input.value);
        });
        this.input.addEventListener('keydown', (event) => this.onInputKeydown(event));
      }
      document.addEventListener('click', this.onDocumentClick);
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onDocumentClick);
      document.removeEventListener('keydown', this.onKeydown);
    }

    onDocumentClick(event) {
      var trigger = event.target.closest('[data-open-picker]');
      if (trigger) {
        event.preventDefault();
        this.open(trigger);
        return;
      }
      if (this.dropdown && !event.target.closest('.hero-destination__search-wrap')) {
        this.dropdown.classList.remove('is-open');
        if (this.searchBar) this.searchBar.classList.remove('is-focused');
      }
    }

    get isOpen() {
      return !!(this.overlay && this.overlay.classList.contains('is-open'));
    }

    getCountries() {
      return window.__TSA_COUNTRIES || [];
    }

    flagHtml(country) {
      if (country.img) {
        return (
          '<span class="hero-destination__grid-flag"><img src="' +
          country.img +
          '" width="24" height="24" alt="" loading="lazy"></span>'
        );
      }
      return '<span class="hero-destination__grid-flag">' + country.flag + '</span>';
    }

    dropdownFlagHtml(country) {
      if (country.img) {
        return (
          '<span class="hero-destination__dropdown-flag"><img src="' +
          country.img +
          '" width="20" height="20" alt="" loading="lazy"></span>'
        );
      }
      return '<span class="hero-destination__dropdown-flag hero-destination__dropdown-flag--emoji">' + country.flag + '</span>';
    }

    buildGrid() {
      if (!this.grid) return;
      var self = this;
      this.grid.innerHTML = this.getCountries()
        .filter(function (c) {
          return c.popular;
        })
        .map(function (c) {
          /* The cell wrapper is not decoration: the tile is clipped, and a
             clipped element eats its own shadow, so depth lives on the
             parent as a filter. Build it here too or the modal's tiles are
             the same shape as the hero's with none of the weight. */
          return (
            '<span class="hero-destination__grid-cell"><a href="' +
            c.url +
            '" class="hero-destination__grid-tile">' +
            self.flagHtml(c) +
            '<span class="hero-destination__grid-name">' +
            c.name +
            '</span></a></span>'
          );
        })
        .join('');
    }

    renderDropdown(query) {
      if (!this.dropdown || !this.searchBar) return;
      var q = query.trim().toLowerCase();
      if (!q) {
        this.dropdown.classList.remove('is-open');
        this.searchBar.classList.remove('is-focused');
        return;
      }
      var self = this;
      var matches = this.getCountries().filter(function (c) {
        return c.name.toLowerCase().indexOf(q) > -1;
      });
      this.searchBar.classList.add('is-focused');
      if (matches.length === 0) {
        this.dropdown.innerHTML = '<div class="hero-destination__dropdown-empty">No destinations found</div>';
      } else {
        this.dropdown.innerHTML = matches
          .map(function (c, i) {
            return (
              '<a href="' +
              c.url +
              '" class="hero-destination__dropdown-item' +
              (i === self.activeIndex ? ' is-active' : '') +
              '"><span class="hero-destination__dropdown-item-left">' +
              self.dropdownFlagHtml(c) +
              '<span>' +
              c.name +
              '</span></span><svg class="hero-destination__dropdown-arrow" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg></a>'
            );
          })
          .join('');
      }
      this.dropdown.classList.add('is-open');
    }

    onInputKeydown(event) {
      if (!this.dropdown) return;
      var items = this.dropdown.querySelectorAll('.hero-destination__dropdown-item');
      if (!items.length) return;
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        this.activeIndex = this.activeIndex < items.length - 1 ? this.activeIndex + 1 : 0;
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        this.activeIndex = this.activeIndex > 0 ? this.activeIndex - 1 : items.length - 1;
      } else if (event.key === 'Enter' && this.activeIndex >= 0) {
        event.preventDefault();
        items[this.activeIndex].click();
        return;
      } else {
        return;
      }
      items.forEach(function (el, i) {
        el.classList.toggle('is-active', i === this.activeIndex);
      }, this);
    }

    open(trigger) {
      if (this.isOpen || !this.overlay || !this.panel) return;
      this.triggerEl = trigger || (document.activeElement instanceof HTMLElement ? document.activeElement : null);
      if (this.triggerEl) this.triggerEl.setAttribute('aria-expanded', 'true');

      this.buildGrid();
      this.overlay.classList.add('is-open');
      this.panel.removeAttribute('aria-hidden');
      this.panel.removeAttribute('inert');

      this.savedScrollY = window.scrollY || window.pageYOffset || 0;
      // Renders inside <main> (hero section), so background inert would exempt all of main; skip inert here, the focus trap already contains keyboard navigation.
      this._prevBodyOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';

      if (this.input) this.input.value = '';
      if (this.dropdown) this.dropdown.classList.remove('is-open');
      if (this.searchBar) this.searchBar.classList.remove('is-focused');
      this.activeIndex = -1;

      document.addEventListener('keydown', this.onKeydown);

      var self = this;
      window.setTimeout(function () {
        if (self.input && typeof self.input.focus === 'function') self.input.focus();
      }, 150);
    }

    close() {
      if (!this.isOpen || !this.overlay || !this.panel) return;
      this.overlay.classList.remove('is-open');
      this.panel.setAttribute('aria-hidden', 'true');
      this.panel.setAttribute('inert', '');

      document.body.style.overflow = this._prevBodyOverflow || '';
      window.scrollTo(0, this.savedScrollY);

      document.removeEventListener('keydown', this.onKeydown);

      var returnFocusTo = this.triggerEl;
      this.triggerEl = null;
      if (returnFocusTo) {
        returnFocusTo.setAttribute('aria-expanded', 'false');
        if (typeof returnFocusTo.focus === 'function') returnFocusTo.focus();
      }
    }

    onKeydown(event) {
      if (event.key === 'Escape' || event.key === 'Esc') {
        event.preventDefault();
        this.close();
        return;
      }
      if (event.key === 'Tab') this.trapFocus(event);
    }

    trapFocus(event) {
      if (!this.panel) return;
      var focusable = getFocusableElements(this.panel);
      if (!focusable.length) return;
      var first = focusable[0];
      var last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      } else if (!this.panel.contains(document.activeElement)) {
        event.preventDefault();
        first.focus();
      }
    }
  }

  if (!customElements.get('tsa-destination-modal')) {
    customElements.define('tsa-destination-modal', TsaDestinationModalElement);
  }
})();
